<template>
  <Toaster position="top-center" />
  <TopBarComponent />

  <RouterView />
  <PlayerComponent ref="player" />
  <CommandPalette v-model="showCommandPalette" />
  <div style="height: 10rem;"></div>

</template>

<script>
import TopBarComponent from './components/TopBarComponent.vue';
import PlayerComponent from './components/PlayerComponent.vue';
import CommandPalette from './components/UI/CommandPalette.vue';
import { Toaster, toast } from 'vue-sonner'
import 'vue-sonner/style.css'

import storageService from '@/scripts/services/StorageService';
import eventBus from '@/scripts/utils/eventBus';
import { settings } from '@/scripts/services/SettingsService';

export default {
  name: 'App',
  components: { Toaster, PlayerComponent, TopBarComponent, CommandPalette },
  beforeMount() {
    // Warm presets without blocking initial route rendering.
    storageService.loadPresets()
  },
  mounted() {
    // eslint-disable-next-line
    console.log("       _                                       _")
    // eslint-disable-next-line
    console.log("    __| | ___ _ __ ___   ___  _ __   __ _ _ __| |_ _   _")
    // eslint-disable-next-line
    console.log("   / _` |/ _ \\ '_ ` _ \\ / _ \\| '_ \\ / _` | '__| __| | | |")
    // eslint-disable-next-line
    console.log("  | (_| |  __/ | | | | | (_) | |_) | (_| | |  | |_| |_| |")
    // eslint-disable-next-line
    console.log("   \\__,_|\\___|_| |_| |_|\\___/| .__/ \\__,_|_|   \\__|\\__, |")
    // eslint-disable-next-line
    console.log("                             |_|                   |___/")
    console.log("")
    console.log("made by @drushadrusha in 2025")

    // Listen for storage service errors (legacy support)
    this.handleStorageError = (errorData) => {
      this.showToast(errorData.message, errorData.description)
    }
    eventBus.$on('storage-error', this.handleStorageError)

    // Listen for general notifications
    this.handleNotification = (notificationData) => {
      this.showNotification(notificationData)
    }
    eventBus.$on('notification', this.handleNotification)

    // Listen for command palette toggle
    this.handleOpenCommandPalette = () => {
      this.showCommandPalette = true
    }
    eventBus.$on('open-command-palette', this.handleOpenCommandPalette)

    // Redirect on logout
    this.handleLogout = () => {
      this.$router.push('/')
    }
    eventBus.$on('logout', this.handleLogout)

    // Command Palette shortcut
    this.setupShortcutHandler()

    // Listen for shortcut changes
    this.handleShortcutChanged = () => {
      this.setupShortcutHandler()
    }
    eventBus.$on('shortcut-changed', this.handleShortcutChanged)
    this.unsubscribeSettings = settings.subscribe((key) => {
      if (key === 'searchShortcut') {
        this.setupShortcutHandler()
      }
    })
  },
  beforeUnmount() {
    // Clean up event listeners
    if (this.handleStorageError) {
      eventBus.$off('storage-error', this.handleStorageError)
    }
    if (this.handleNotification) {
      eventBus.$off('notification', this.handleNotification)
    }
    if (this.handleOpenCommandPalette) {
      eventBus.$off('open-command-palette', this.handleOpenCommandPalette)
    }
    if (this.handleLogout) {
      eventBus.$off('logout', this.handleLogout)
    }
    if (this.handleCommandPalette) {
      window.removeEventListener('keydown', this.handleCommandPalette)
    }
    if (this.handleShortcutChanged) {
      eventBus.$off('shortcut-changed', this.handleShortcutChanged)
    }
    if (this.unsubscribeSettings) {
      this.unsubscribeSettings()
      this.unsubscribeSettings = null
    }
  },
  data() {
    return {
      showCommandPalette: false
    }
  },
  methods: {
    setupShortcutHandler() {
      // Remove old handler if exists
      if (this.handleCommandPalette) {
        window.removeEventListener('keydown', this.handleCommandPalette)
      }

      const shortcut = settings.get('searchShortcut') || { key: 'tab', ctrlKey: false, metaKey: false, altKey: false, shiftKey: false }

      this.handleCommandPalette = (e) => {
        const matchesKey = e.key.toLowerCase() === shortcut.key.toLowerCase()
        const matchesCtrl = shortcut.ctrlKey ? e.ctrlKey : true
        const matchesMeta = shortcut.metaKey ? e.metaKey : true
        const matchesAlt = shortcut.altKey ? e.altKey : !e.altKey
        const matchesShift = shortcut.shiftKey ? e.shiftKey : !e.shiftKey

        if (matchesKey && matchesCtrl && matchesMeta && matchesAlt && matchesShift) {
          e.preventDefault()
          this.showCommandPalette = !this.showCommandPalette
        }
      }
      window.addEventListener('keydown', this.handleCommandPalette)
    },
    showToast(message, description) {
      toast.message(message, {
          class: 'toast',
        description: description,
      })
    },
    showNotification(notificationData) {
      const { message, description } = notificationData

      // Use single toast type
      const toastOptions = {
        description: description,
        class: 'toast',
        duration: 10000,
      }

      toast(message, toastOptions)
    }
  }
}

</script>

<style>
body {
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: radial-gradient(circle,rgba(31, 31, 31, 1) 0%, rgba(26, 26, 26, 1) 50%);
  background-color: var(--primary-bg-color);
  background-attachment: fixed;
  background-size: cover;
  background-repeat: no-repeat;
  min-height: 100vh;
  color: var(--primary-text-color);
  --primary-bg-color: #1A1A1A;
  --secondary-bg-color: #2F2F2F;
  --primary-text-color: #e4e4e4;
  --secondary-text-color: #999999;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  outline: 0;
  padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) env(safe-area-inset-bottom,
      0px) env(safe-area-inset-left, 0px);
}

#app {
  height: 100vh;
}

.doto {
  font-family: "Doto", sans-serif;
  font-weight: 900;
}

.toast {
  background: rgba(47, 47, 47, 0.35) !important;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(47, 47, 47, 0.61) !important;
  backdrop-filter: blur(12.3px) !important;
  -webkit-backdrop-filter: blur(12.3px) !important;
  border-radius: 16px !important;
  color: #eeeeee !important;
}
.toast [data-description] {
  color: #cccccc !important;
}
.btn-new {
    display: inline-block;
    cursor: pointer;
    color: var(--primary-text-color);
    font-weight: 600;
    font-size: 0.875rem;
    padding-inline: 0.75rem;
    padding-block: 0.4rem;
    background: rgba(47, 47, 47, 0.8);
    border: 1px solid rgba(47, 47, 47, 1);
    border-radius: 0.375rem;
    box-shadow: inset 0 2px 4px 0 #ffffff06;
    transition: box-shadow 240ms cubic-bezier(.2, .8, .2, 1),
    transform 240ms cubic-bezier(.2, .8, .2, 1),
    background-color 240ms ease;
}

.btn-notransform:hover{
  transform: none !important;
}

.btn_danger {
    color: rgb(210, 47, 47);
}

.btn-new:hover {
  background-color: #393939;
  box-shadow: rgba(39, 39, 39, 0.15) 0px 5px 6px 0px;
  transform: scale(1.01);
}

.context_menu {
    max-width: 440px;
    overflow: auto;
    background: rgba(47, 47, 47, 0.8);
    padding:0.25rem;
    border: 1px solid rgba(47, 47, 47, 1);
    border-radius: 0.75rem;
    width: 13rem;
    backdrop-filter: blur(12.3px);
    -webkit-backdrop-filter: blur(12.3px);
    box-shadow: inset 0 2px 4px 0 #ffffff06;
}

.context_menu_item {
    padding-inline: 0.75rem;
    font-weight: 600;
    padding-block: 0.4rem;
    border-radius: 0.75rem;
    cursor: pointer;
}

.context_menu_item:hover {
    background-color: #393939;
}

.context_menu_separator {
    margin-block: 0.25rem;
    height: 1px;
    background-color: rgb(72, 72, 72);
}

.context_menu_icon {
  color: #a3a3a3;
  margin-right: 0.25rem;
}

.tab_container {
    display: flex;
    gap: 1rem;
    width: 100%;
    justify-content: space-around;
}

.tab_container_left_aligned {
    display: flex;
    gap: 1rem;
    width: 100%;
    justify-content: flex-start;
}

.tab_item {
    border-radius: 3.40282e+38px;
    padding-inline: 0.75rem;
    padding-block: 0.25rem;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.875rem;
    border: 1px solid rgba(47, 47, 47, 0);
    transition: box-shadow 240ms cubic-bezier(.2, .8, .2, 1), background-color 240ms ease;
}

.tab_item:hover {
    background-color: #3d3d3d28;
}

.tab_item_active {
    background-color: rgba(47, 47, 47, 0.8);
    border: 1px solid rgba(47, 47, 47, 1);
    box-shadow: inset 0 2px 4px 0 #ffffff06;
}
.tab_item_active:hover {
    background-color: rgba(47, 47, 47, 0.8);
}

.card_thing {
    padding:1.5rem;
    background: rgba(33, 33, 33, 0.57);
    border-radius: 16px;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(12.9px);
    -webkit-backdrop-filter: blur(12.9px);
    border: 1px solid rgba(47, 47, 47, 0.59);
    box-shadow: inset 0 2px 4px 0 #ffffff06;
    border-radius: 0.75rem;
}

.chip_thing {
    font-family: 'Doto', sans-serif;
    display: inline-block;
    border-radius: 3.40282e+38px;
    padding-inline: 0.75rem;
    padding-block: 0.05rem;
    font-weight: 900;
    font-size: 0.75rem;
    background-color: rgba(47, 47, 47, 0.8);
    border: 1px solid rgba(47, 47, 47, 1);
    box-shadow: inset 0 2px 4px 0 #ffffff06;
    transition: box-shadow 240ms cubic-bezier(.2, .8, .2, 1);
    margin-right: 0.5rem;
}

.chip_thing_two {
    display: inline-block;
    border-radius: 3.40282e+38px;
    font-weight: 600;
    font-size: 0.8rem;
    color: rgba(162, 162, 162, 0.8);
    transition: box-shadow 240ms cubic-bezier(.2, .8, .2, 1);
    margin-right: 0.5rem;
}

.chip_thing_orange {
    box-shadow: inset 0 4px 4px 0 #d48003af;
}

.input_thing {
    display: block;
    width: 100%;
    color: var(--primary-text-color);
    font-size: 1rem;
    font-weight: 500;
    line-height: 1.5;
    appearance: none;
    background-clip: padding-box;
    background-color: #ffffff05;
    border-radius: 0.5rem;
    border-style: none;
    padding-inline: 0.75rem;
    padding-block: 0.4rem;
    border: 1px solid rgba(96, 96, 96, 0);
}
.input_thing:focus{
    border: 1px solid rgb(96, 96, 96);
    outline:none;
}

.text_sec{
    color: #a3a3a3;
}

.text_sec2 {
  color: rgba(162, 162, 162, 0.8);
}


.project-title {
    padding-top: 0.5rem;
    font-size: 1.5rem;
    font-weight: 600;
}

.project-subtitle {
    font-size: 1rem;
}


</style>
